import React, { useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import {
  FIELD_GROUPS, CATEGORIES, OPERATORS, RULE_TYPES,
  newCond, newGroup, fieldDef, buildRuleJSON, validateRule, evalGroup,
  makeTemplate, TEMPLATES, SAMPLE_CONTEXT,
  getActionTargetFields,
  enrichFieldGroups, resolveOperators, parseTimeRangeValue,
} from "./engine";
import { evaluateManual } from "./billing-rules-api";
import { searchChargeCaptureServices } from "../ipd-billing/billing-api";
import {
  Btn, CardButton, Check, DateInput, DateTimeInput, Field, Input, Select, TabBar, TextArea,
  TimeInput, cardStyle, labelStyle, C, RuleTypeChip,
} from "./ui";
import SvgIcon from "../ipd-billing/SvgIcon";
import { Modal } from "./ui";

const ACTION_KINDS = [
  { key: "price_override", label: "Price Override" },
  { key: "discount", label: "Discount" },
  { key: "tax", label: "Tax" },
  { key: "markup", label: "Markup" },
  { key: "approval", label: "Approval" },
  { key: "remark", label: "Remark" },
  { key: "set_billing_category", label: "Set Billing Category" },
  { key: "doctor_payout", label: "Doctor Payout" },
];

const SIM_PRESETS = {
  "OPD senior": { patient: { age: 67, gender: "FEMALE", metadata: { vip: false } }, encounter: { encounter_type: "OPD", payer_type: "SELF" }, charge_event: { category: "CONSULTATION", service_code: "GENERAL-CONSULT", unit_price: 1000, quantity: 1, gross_amount: 1000 } },
  "IPD TPA ICU": { patient: { age: 54, gender: "MALE", metadata: { vip: false } }, encounter: { encounter_type: "IPD", payer_type: "TPA" }, charge_event: { category: "ROOM_RENT", service_code: "ICU-BED-DAY", unit_price: 14500, quantity: 1, gross_amount: 14500 } },
  VIP: { patient: { age: 42, gender: "MALE", metadata: { vip: true } }, encounter: { encounter_type: "IPD", payer_type: "SELF" }, charge_event: { category: "PROCEDURE", service_code: "MRI-BRAIN", unit_price: 12000, quantity: 1, gross_amount: 12000 } },
  "High value": { patient: { age: 50, gender: "MALE", metadata: { vip: false } }, encounter: { encounter_type: "IPD", payer_type: "INSURANCE" }, charge_event: { category: "PROCEDURE", service_code: "PCI", unit_price: 95000, quantity: 1, gross_amount: 95000 } },
};

function updateNode(root, id, fn) {
  const recur = (n) => {
    if (n.id === id) return fn(n);
    if (n.kind === "group") {
      const next = [];
      for (const c of n.children) {
        const r = recur(c);
        if (r) next.push(r);
      }
      return { ...n, children: next };
    }
    return n;
  };
  return recur(root);
}

function addChildToGroup(root, groupId, child) {
  return updateNode(root, groupId, (n) => {
    if (n.kind !== "group") return n;
    return { ...n, children: [...n.children, child] };
  });
}

const SERVICE_FIELDS = ["charge_event.service_code", "charge_event.service_name"];
const PRACTITIONER_FIELDS = ["encounter.provider_id", "encounter.provider_name_snapshot"];
// Picked in the dropdown, never saved: choosing it rewrites the condition to "is not empty".
const ALL_PRACTITIONERS = "__ALL_PRACTITIONERS__";

/**
 * Searchable picker for a real billing service, so a service condition value is
 * chosen (not free-typed and mistyped). For the "Service Code" field it stores the
 * service_code; for "Service Name" it stores the service name. Uses the same
 * charge-capture services list the billing screen uses.
 */
function ServiceValueInput({ field, value, onChange }) {
  const useCode = field === "charge_event.service_code";
  const [q, setQ] = useState(String(value ?? ""));
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setQ(String(value ?? ""));
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const list = await searchChargeCaptureServices(q, { limit: 40 });
        // De-duplicate so one clinical service shows once, not once per bed variant.
        // Bed variants share the same name (differ only by service_code): key the
        // "Service Name" list by name, the "Service Code" list by code.
        const seen = new Set();
        const uniq = [];
        (Array.isArray(list) ? list : []).forEach((s) => {
          const key = useCode ? (s.service_code || s.code || "") : (s.name || "");
          if (!key || seen.has(key)) return;
          seen.add(key);
          uniq.push(s);
        });
        if (alive) setOpts(uniq);
      } catch (e) {
        if (alive) setOpts([]);
      } finally {
        if (alive) setLoading(false);
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open]);

  const labelFor = (s) => (useCode ? (s.service_code || s.code || "") : (s.name || ""));

  return (
    <Autocomplete
      // Combobox: typing sets the value directly (still works if the list can't
      // load / the service isn't listed); picking a suggestion fills the exact one.
      freeSolo
      disableClearable
      forcePopupIcon={false}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={opts}
      loading={loading}
      filterOptions={(x) => x}
      inputValue={q}
      onInputChange={(e, next, reason) => {
        if (reason === "reset") return;
        setQ(next);
        onChange(next);
      }}
      getOptionLabel={(s) => (typeof s === "string" ? s : labelFor(s))}
      onChange={(e, s) => { if (s && typeof s !== "string") onChange(labelFor(s)); }}
      noOptionsText="No services found"
      loadingText="Searching…"
      renderOption={(props, s) => (
        <li {...props} key={s.id || s.service_code || s.name} style={{ display: "block", padding: "6px 10px" }}>
          <div style={{ fontWeight: 600, fontSize: 12 }}>{s.name}</div>
          <div style={{ fontFamily: "monospace", fontSize: 10, color: C.textSub }}>
            {useCode ? (s.service_code || s.code || "") : "all bed types"}
          </div>
        </li>
      )}
      renderInput={(params) => (
        <Input
          {...params}
          placeholder={useCode ? "Search / type service code…" : "Search / type service name…"}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 40,
              minHeight: 40,
              padding: "0 !important",
            },
            "& .MuiOutlinedInput-input": { padding: "8.5px 12px !important" },
          }}
        />
      )}
      sx={{ width: 220 }}
    />
  );
}

/** The value of a list condition, always as an array - that is what the rule engine reads. */
function asList(value) {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  return String(value ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Several values from a fixed list - practitioners, service groups, classifications.
 *
 * "In list" used to be a comma-separated text box, so choosing four service groups meant
 * typing four ids by hand and getting no warning when one was wrong. Picking them is the
 * whole point of a list operator.
 */
function MultiOptionInput({ def, value, onChange }) {
  const byValue = new Map((def.options || []).map((o) => [String(o.value), o]));
  const selected = asList(value)
    .map((v) => byValue.get(String(v)) || { value: v, label: v })
    .filter(Boolean);
  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={def.options || []}
      value={selected}
      isOptionEqualToValue={(o, v) => String(o.value) === String(v.value)}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.label ?? String(o.value))}
      onChange={(e, picked) => onChange(picked.map((o) => String(o.value)))}
      renderInput={(params) => (
        <Input {...params} placeholder={selected.length ? "" : "Pick one or more…"} title={def.hint || ""} />
      )}
      sx={{ width: 320 }}
    />
  );
}

/**
 * Several real services at once, searched the same way the single picker searches.
 * Stores codes or names to match the field the condition is on.
 */
function MultiServiceValueInput({ field, value, onChange }) {
  const useCode = field === "charge_event.service_code";
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const selected = asList(value);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const list = await searchChargeCaptureServices(q, { limit: 40 });
        const seen = new Set();
        const uniq = [];
        (Array.isArray(list) ? list : []).forEach((svc) => {
          const key = useCode ? (svc.service_code || svc.code || "") : (svc.name || "");
          if (!key || seen.has(key)) return;
          seen.add(key);
          uniq.push(key);
        });
        if (alive) setOpts(uniq);
      } catch (err) {
        if (alive) setOpts([]);
      } finally {
        if (alive) setLoading(false);
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open, useCode]);

  return (
    <Autocomplete
      multiple
      freeSolo
      disableCloseOnSelect
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={opts}
      loading={loading}
      filterOptions={(x) => x}
      value={selected}
      inputValue={q}
      onInputChange={(e, next, reason) => { if (reason !== "reset") setQ(next); }}
      onChange={(e, picked) => onChange(picked.map(String))}
      noOptionsText="No services found"
      loadingText="Searching…"
      renderInput={(params) => (
        <Input
          {...params}
          placeholder={selected.length ? "" : (useCode ? "Search service codes…" : "Search service names…")}
        />
      )}
      sx={{ width: 320 }}
    />
  );
}

// Prefer the live catalog's field definition (it carries the department/doctor `options`
// fetched per tenant); fall back to the local static catalog when it isn't loaded yet.
function findFieldDef(fieldGroups, path) {
  for (const group of fieldGroups || []) {
    const found = (group.fields || []).find((f) => f.path === path);
    if (found) return found;
  }
  return fieldDef(path);
}

function ValueInput({ cond, onChange, onCondChange, operators, fieldGroups }) {
  const def = findFieldDef(fieldGroups, cond.field);
  const op = (operators ?? OPERATORS).find((o) => o.code === cond.operator);
  if (!op || op.arity === "none") return null;

  if (op.arity === "time_range") {
    const range = parseTimeRangeValue(cond.value);
    return (
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <TimeInput
          value={range.from}
          onChange={(v) => onChange({ from: v, to: range.to })}
          width={130}
        />
        <span style={{ fontSize: 11, color: C.textSub }}>to</span>
        <TimeInput
          value={range.to}
          onChange={(v) => onChange({ from: range.from, to: v })}
          width={130}
        />
      </div>
    );
  }

  if (op.arity === "list") {
    // Anything with a known set of values gets a real multi-select; a service gets the
    // searchable one. Free text stays available for fields that are neither.
    if (def?.options?.length) {
      return <MultiOptionInput def={def} value={cond.value} onChange={onChange} />;
    }
    if (SERVICE_FIELDS.includes(cond.field)) {
      return <MultiServiceValueInput field={cond.field} value={cond.value} onChange={onChange} />;
    }
    return (
      <Input
        value={Array.isArray(cond.value) ? cond.value.join(",") : String(cond.value ?? "")}
        onChange={(e) => onChange(e.target.value)}
        placeholder="comma,separated"
        width={200}
      />
    );
  }
  if (op.arity === "two") {
    const arr = Array.isArray(cond.value) ? cond.value : String(cond.value ?? "").split(",");
    return (
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <Input value={String(arr[0] ?? "")} onChange={(e) => onChange([e.target.value, arr[1] ?? ""])} placeholder="from" width={100} />
        <span style={{ fontSize: 11, color: C.textSub }}>to</span>
        <Input value={String(arr[1] ?? "")} onChange={(e) => onChange([arr[0] ?? "", e.target.value])} placeholder="to" width={100} />
      </div>
    );
  }
  if (def?.type === "boolean") {
    return (
      <Select value={String(cond.value)} onChange={(e) => onChange(e.target.value === "true")} width={110}>
        <MenuItem value="true">true</MenuItem>
        <MenuItem value="false">false</MenuItem>
      </Select>
    );
  }
  // Department / practitioner pickers. Options come live from the catalog so the saved value
  // is the real UUID billing matches on — a hand-typed name or id silently matches no charge.
  if (def?.options?.length) {
    const isPractitioner = PRACTITIONER_FIELDS.includes(cond.field);
    return (
      <Select
        value={String(cond.value ?? "")}
        onChange={(e) => {
          // "All practitioners" is not a value — it is the absence of one. Saved as
          // "is not empty", it matches every charge that has a practitioner on it, and
          // scores as the broad rule it is, so any rule naming one practitioner beats it.
          if (e.target.value === ALL_PRACTITIONERS) {
            onCondChange?.({ ...cond, operator: "is_not_null", value: "" });
            return;
          }
          onChange(e.target.value);
        }}
        title={def.hint || ""}
        width={240}
      >
        <MenuItem value="">Pick…</MenuItem>
        {isPractitioner && (
          <MenuItem value={ALL_PRACTITIONERS}>All practitioners</MenuItem>
        )}
        {def.options.map((o) => (
          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
        ))}
      </Select>
    );
  }
  if (def?.type === "enum" && def.enumValues) {
    return (
      <Select value={String(cond.value ?? "")} onChange={(e) => onChange(e.target.value)} width={170}>
        <MenuItem value="">Pick…</MenuItem>
        {def.enumValues.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
      </Select>
    );
  }
  if (def?.type === "number") {
    return <Input type="number" value={String(cond.value ?? "")} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} width={130} />;
  }
  if (def?.type === "date") {
    return <DateInput value={String(cond.value ?? "")} onChange={(v) => onChange(v)} width={175} />;
  }
  if (def?.type === "datetime") {
    return <DateTimeInput value={String(cond.value ?? "")} onChange={(v) => onChange(v)} width={225} />;
  }
  // Service Code / Service Name → pick a real service instead of free-typing it
  // (single-value operators only; list/range operators are handled above).
  if (SERVICE_FIELDS.includes(cond.field)) {
    return <ServiceValueInput field={cond.field} value={cond.value} onChange={onChange} />;
  }
  return <Input value={String(cond.value ?? "")} onChange={(e) => onChange(e.target.value)} placeholder="value" width={170} />;
}

function defaultValueForOperator(opCode) {
  const op = OPERATORS.find((o) => o.code === opCode);
  if (op?.arity === "time_range") return { from: "", to: "" };
  if (op?.arity === "two") return ["", ""];
  // A list operator starts as an empty list, not an empty string - the multi-select reads
  // its selection straight off the value, and a string would look like one blank entry.
  if (op?.arity === "list") return [];
  return "";
}

function CondRow({ cond, onChange, fieldGroups, operators }) {
  const def = findFieldDef(fieldGroups, cond.field);
  const op = operators.find((o) => o.code === cond.operator);
  return (
    <div style={{ ...cardStyle, padding: 10, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", opacity: cond.enabled ? 1 : 0.5 }}>
      <Check checked={cond.enabled} onChange={(e) => onChange({ ...cond, enabled: e.target.checked })} />
      <Select value={cond.field || ""} onChange={(e) => onChange({ ...cond, field: e.target.value, value: "" })} width={200}>
        <MenuItem value="">Pick field…</MenuItem>
        {fieldGroups.flatMap((g) => [
          <ListSubheader key={g.key}>{g.label}</ListSubheader>,
          ...g.fields.map((f) => <MenuItem key={f.path} value={f.path}>{f.label}</MenuItem>),
        ])}
      </Select>
      <Select
        value={cond.operator}
        onChange={(e) => {
          const nextOp = e.target.value;
          onChange({
            ...cond,
            operator: nextOp,
            value: nextOp === cond.operator ? cond.value : defaultValueForOperator(nextOp),
          });
        }}
        width={170}
      >
        {operators.map((o) => <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>)}
      </Select>
      <ValueInput
        cond={cond}
        operators={operators}
        fieldGroups={fieldGroups}
        onChange={(v) => onChange({ ...cond, value: v })}
        onCondChange={onChange}
      />
      {def && <span title={def.hint || ""} style={{ fontSize: 10, fontWeight: 700, color: C.textSub, textTransform: "uppercase" }}>{def.type}</span>}
      {op?.arity === "none" && <span style={{ fontSize: 10, color: C.textSub }}>no value</span>}
      <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
        <Btn variant="ghost" size="sm" onClick={() => onChange(null)}><SvgIcon name="trash" size={14} color={C.danger} /></Btn>
      </div>
    </div>
  );
}

function GroupView({ node, onChange, depth = 0, isRoot, fieldGroups, operators }) {
  const [dragOver, setDragOver] = useState(false);
  const tone = depth % 2 === 0 ? { border: "#bae6fd", bg: "#f0f9ff" } : { border: "#99f6e4", bg: "#f0fdfa" };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const path = e.dataTransfer.getData("application/x-rule-field");
    if (!path) return;
    onChange(addChildToGroup(node, node.id, newCond(path, "eq")));
  };

  const updateChild = (childId, next) => {
    onChange(updateNode(node, childId, () => next));
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      style={{
        border: `2px dashed ${dragOver ? C.primary : tone.border}`,
        background: tone.bg, borderRadius: 10, padding: 12,
        outline: dragOver ? `2px solid ${C.primary}` : "none",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <Select
          value={node.type}
          onChange={(e) => onChange({ ...node, type: e.target.value })}
          width={100}
          sx={{ "& .MuiSelect-select": { fontWeight: 700 } }}
        >
          <MenuItem value="all">AND</MenuItem>
          <MenuItem value="any">OR</MenuItem>
          <MenuItem value="not">NOT</MenuItem>
        </Select>
        <span style={{ fontSize: 11, color: C.textSub }}>
          {node.type === "all" ? "All must match" : node.type === "any" ? "Any can match" : "None must match"}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <Btn variant="ghost" size="sm" onClick={() => onChange({ ...node, children: [...node.children, newCond()] })}>
            <SvgIcon name="plus" size={12} color={C.primary} /> Condition
          </Btn>
          <Btn variant="ghost" size="sm" onClick={() => onChange({ ...node, children: [...node.children, newGroup("all")] })}>
            <SvgIcon name="plus" size={12} color={C.primary} /> Group
          </Btn>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {node.children.length === 0 && (
          <div style={{ textAlign: "center", padding: 20, fontSize: 12, color: C.textSub, border: `1px dashed ${C.border}`, borderRadius: 8 }}>
            Drop a field here or add a condition
          </div>
        )}
        {node.children.map((c) =>
          c.kind === "cond" ? (
            <CondRow key={c.id} cond={c} onChange={(next) => updateChild(c.id, next)} fieldGroups={fieldGroups} operators={operators} />
          ) : (
            <GroupView key={c.id} node={c} onChange={(g) => updateChild(c.id, g)} depth={depth + 1} fieldGroups={fieldGroups} operators={operators} />
          ),
        )}
      </div>
    </div>
  );
}

function FieldsSidebar({ fieldGroups, categories }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const groups = fieldGroups.filter((g) => cat === "all" || g.category === cat);

  return (
    <aside style={{ ...cardStyle, display: "flex", flexDirection: "column", maxHeight: 520 }}>
      <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Billing Data Fields</div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search field…" sx={{ marginBottom: "8px" }} />
        <Select value={cat} onChange={(e) => setCat(e.target.value)}>
          <MenuItem value="all">All categories</MenuItem>
          {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </Select>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 8 }}>
        {groups.map((g) => {
          const fields = g.fields.filter((f) =>
            !q || f.label.toLowerCase().includes(q.toLowerCase()) || f.path.toLowerCase().includes(q.toLowerCase()),
          );
          if (!fields.length) return null;
          return (
            <div key={g.key} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textSub, padding: "4px 6px" }}>{g.label}</div>
              {fields.map((f) => (
                <div
                  key={f.path}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("application/x-rule-field", f.path);
                    e.dataTransfer.effectAllowed = "copy";
                  }}
                  style={{
                    padding: "8px 10px", marginBottom: 4, borderRadius: 8, border: `1px solid ${C.border}`,
                    background: "#fff", cursor: "grab", fontSize: 11,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{f.label}</div>
                  <div style={{ fontFamily: "monospace", fontSize: 10, color: C.textSub }}>{f.path}</div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

function BlocksSidebar() {
  return (
    <aside style={{ ...cardStyle, padding: 12, fontSize: 11 }}>
      <div style={{ fontWeight: 700, marginBottom: 10 }}>Building Blocks</div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ ...labelStyle, marginBottom: 6 }}>Connectors</div>
        <div style={{ display: "flex", gap: 6 }}>
          {["AND", "OR", "NOT"].map((l) => (
            <span key={l} style={{ flex: 1, textAlign: "center", padding: "6px 0", borderRadius: 6, background: C.accent, color: C.primary, fontWeight: 700 }}>{l}</span>
          ))}
        </div>
      </div>
      <div>
        <div style={{ ...labelStyle, marginBottom: 6 }}>Operators</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {OPERATORS.map((o) => (
            <span key={o.code} style={{ padding: "2px 6px", borderRadius: 4, background: "#f1f5f9", fontSize: 10 }}>{o.label}</span>
          ))}
        </div>
      </div>
    </aside>
  );
}

function MetadataForm({ rule, update, ruleTypes }) {
  return (
    <div style={{ ...cardStyle, padding: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Rule Metadata</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
        <Field label="Rule Code *"><Input value={rule.rule_code} mono onChange={(e) => update({ rule_code: e.target.value })} /></Field>
        <Field label="Rule Name *"><Input value={rule.rule_name} onChange={(e) => update({ rule_name: e.target.value })} /></Field>
        <Field label="Rule Type *">
          <Select value={rule.rule_type} onChange={(e) => update({ rule_type: e.target.value })}>
            {ruleTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </Field>
        <Field label="Priority *"><Input type="number" value={rule.priority} onChange={(e) => update({ priority: Number(e.target.value) })} /></Field>
        <Field label="Effective From *">
          <DateInput value={rule.effective_from} onChange={(v) => update({ effective_from: v })} required />
        </Field>
        <Field label="Effective To">
          <DateInput value={rule.effective_to ?? ""} minDate={rule.effective_from} onChange={(v) => update({ effective_to: v || null })} />
        </Field>
        <Field label="Description" style={{ gridColumn: "1 / -1" }}>
          <TextArea value={rule.description} onChange={(e) => update({ description: e.target.value })} />
        </Field>
        <Check checked={rule.is_active} onChange={(e) => update({ is_active: e.target.checked })} label="Active" />
        <Check checked={rule.stop_processing} onChange={(e) => update({ stop_processing: e.target.checked })} label="Stop Processing" />
        <Field label="metadata.business_policy" style={{ gridColumn: "1 / -1" }}>
          <Input
            value={rule.metadata?.business_policy ?? ""}
            mono
            onChange={(e) => {
              const meta = { ...(rule.metadata || {}), source: rule.metadata?.source ?? "rule_builder_ui" };
              const bp = e.target.value.trim();
              if (bp) meta.business_policy = bp;
              else delete meta.business_policy;
              update({ metadata: meta });
            }}
          />
        </Field>
      </div>
    </div>
  );
}

function TargetFieldSelect({ value, onChange, actionTargetFields, hint }) {
  const target = value ?? "";
  const groups = useMemo(() => {
    const order = [];
    const map = {};
    actionTargetFields.forEach((f) => {
      if (!map[f.group]) {
        map[f.group] = [];
        order.push(f.group);
      }
      map[f.group].push(f);
    });
    return order.map((g) => ({ label: g, fields: map[g] }));
  }, [actionTargetFields]);

  return (
    <Field label="Target field *">
      <Select value={target} onChange={(e) => onChange(e.target.value)}>
        {!target && <MenuItem value="">Select field…</MenuItem>}
        {target && !actionTargetFields.some((f) => f.path === target) && (
          <MenuItem value={target}>{target} (saved)</MenuItem>
        )}
        {groups.flatMap((g) => [
          <ListSubheader key={g.label}>{g.label}</ListSubheader>,
          ...g.fields.map((f) => (
            <MenuItem key={f.path} value={f.path}>
              {f.label}
            </MenuItem>
          )),
        ])}
      </Select>
      {hint && <div style={{ fontSize: 10, color: C.textSub, marginTop: 4 }}>{hint}</div>}
    </Field>
  );
}

function ActionEditor({ kind, value, onChange, actionTargetFields }) {
  const v = value ?? {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  const row = (k, label, props = {}) => (
    <Field label={label}><Input value={v[k] ?? ""} onChange={(e) => set(k, e.target.value)} {...props} /></Field>
  );

  const withTarget = (body, hint) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <TargetFieldSelect
        value={v.target_field ?? ""}
        onChange={(path) => set("target_field", path)}
        actionTargetFields={actionTargetFields}
        hint={hint}
      />
      {body}
    </div>
  );

  if (kind === "price_override") {
    return withTarget(
      <>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {row("unit_price", "Unit price *", { type: "number", inputProps: { step: "0.01" } })}
          {row("currency_code", "Currency (optional)")}
        </div>
        <Field label="Reason">
          <TextArea value={v.reason ?? ""} onChange={(e) => set("reason", e.target.value)} />
        </Field>
      </>,
      "Field whose value this action sets (usually unit price on the charge line).",
    );
  }
  if (kind === "discount") {
    return withTarget(
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <Field label="Discount type *">
          <Select value={v.discount_type ?? "PERCENTAGE"} onChange={(e) => set("discount_type", e.target.value)}>
            <MenuItem value="PERCENTAGE">PERCENTAGE</MenuItem>
            <MenuItem value="FIXED">FIXED</MenuItem>
          </Select>
        </Field>
        {row("discount_value", "Value *", { type: "number" })}
        {row("max_discount_amount", "Max amount (optional)", { type: "number" })}
        {row("reason", "Reason")}
      </div>,
      "Amount field the discount is calculated against.",
    );
  }
  if (kind === "tax") {
    return withTarget(
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {row("tax_percentage", "Tax % *", { type: "number" })}
        {row("tax_type", "Tax type")}
        {row("reason", "Reason")}
      </div>,
      "Field the tax is applied to.",
    );
  }
  if (kind === "markup") {
    return withTarget(
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {row("markup_percentage", "Markup % *", { type: "number" })}
        {row("reason", "Reason")}
      </div>,
      "Field the markup adjusts.",
    );
  }
  if (kind === "approval") {
    return withTarget(
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Required *">
          <Select value={String(v.approval_required ?? true)} onChange={(e) => set("approval_required", e.target.value === "true")}>
            <MenuItem value="true">true</MenuItem>
            <MenuItem value="false">false</MenuItem>
          </Select>
        </Field>
        {row("approval_role", "Approval role *")}
        {row("approval_reason", "Reason")}
      </div>,
      "Charge field that triggers approval.",
    );
  }
  if (kind === "remark") {
    return withTarget(
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
        <Field label="Message *">
          <TextArea value={v.message ?? ""} onChange={(e) => set("message", e.target.value)} />
        </Field>
        <Field label="Severity *">
          <Select value={v.severity ?? "INFO"} onChange={(e) => set("severity", e.target.value)}>
            <MenuItem value="INFO">INFO</MenuItem>
            <MenuItem value="WARNING">WARNING</MenuItem>
            <MenuItem value="ERROR">ERROR</MenuItem>
          </Select>
        </Field>
      </div>,
      "Context field this remark is attached to.",
    );
  }
  if (kind === "set_billing_category") {
    return withTarget(
      row("category", "Category value *"),
      "Field to write the billing category into.",
    );
  }
  if (kind === "doctor_payout") {
    // No target_field — the payee comes from recipient_provider_field.
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Doctor role *">
            <Select value={v.payout_role ?? "ADMITTING_DOCTOR"} onChange={(e) => set("payout_role", e.target.value)}>
              <MenuItem value="ADMITTING_DOCTOR">ADMITTING_DOCTOR</MenuItem>
              <MenuItem value="REFERRING_DOCTOR">REFERRING_DOCTOR</MenuItem>
              <MenuItem value="PERFORMING_DOCTOR">PERFORMING_DOCTOR</MenuItem>
              <MenuItem value="CONSULTING_DOCTOR">CONSULTING_DOCTOR</MenuItem>
            </Select>
          </Field>
          <Field label="Pay who (recipient field) *">
            <Select value={v.recipient_provider_field ?? "encounter.provider_id"} onChange={(e) => set("recipient_provider_field", e.target.value)}>
              <MenuItem value="encounter.provider_id">Admitting doctor (encounter.provider_id)</MenuItem>
              <MenuItem value="charge_event.referred_by_provider_id">Referred doctor (charge_event.referred_by_provider_id)</MenuItem>
              <MenuItem value="charge_event.performed_by_provider_id">Performing doctor (charge_event.performed_by_provider_id)</MenuItem>
            </Select>
          </Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Payout type *">
            <Select value={v.payout_type ?? "PERCENTAGE"} onChange={(e) => set("payout_type", e.target.value)}>
              <MenuItem value="PERCENTAGE">PERCENTAGE</MenuItem>
              <MenuItem value="FIXED">FIXED</MenuItem>
            </Select>
          </Field>
          {row("payout_value", "Value * (% or amount)", { type: "number", inputProps: { step: "0.01" } })}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Calculated on (basis) *">
            <Select value={v.basis_amount ?? "ledger.net_amount"} onChange={(e) => set("basis_amount", e.target.value)}>
              <MenuItem value="ledger.net_amount">Net amount (what patient pays)</MenuItem>
              <MenuItem value="ledger.gross_amount">Gross amount (before discount)</MenuItem>
              <MenuItem value="charge_event.unit_price">Unit price (per unit, before quantity)</MenuItem>
              <MenuItem value="package.consultation_amount">Consultation price inside the package</MenuItem>
            </Select>
          </Field>
          {row("tds_percentage", "TDS % (tax withheld)", { type: "number", inputProps: { step: "0.01" } })}
        </div>
      </div>
    );
  }
  return null;
}

function ActionsPanel({ actions, setActions, stopProcessing, setStopProcessing, actionTargetFields }) {
  const present = ACTION_KINDS.filter((k) => actions[k.key]);
  const missing = ACTION_KINDS.filter((k) => !actions[k.key]);
  const defaults = {
    price_override: {
      unit_price: "",
      currency_code: "",
      reason: "",
    },
    discount: {
      discount_type: "PERCENTAGE",
      discount_value: "",
      max_discount_amount: "",
      reason: "",
    },
    tax: {
      target_field: "charge_event.gross_amount",
      tax_percentage: "",
      tax_type: "GST",
      reason: "",
    },
    markup: {
      markup_percentage: "",
      reason: "",
    },
    approval: {
      approval_required: true,
      approval_role: "Billing Manager",
      approval_reason: "",
    },
    remark: {
      message: "",
      severity: "INFO",
    },
    set_billing_category: {
      category: "",
    },
    doctor_payout: {
      payout_role: "ADMITTING_DOCTOR",
      payout_type: "PERCENTAGE",
      payout_value: "",
      basis_amount: "ledger.net_amount",
      tds_percentage: "",
      recipient_provider_field: "encounter.provider_id",
    },
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 16 }}>
      <div>
        <div style={{ ...cardStyle, padding: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>THEN apply these actions</div>
          {present.length === 0 && (
            <div style={{ textAlign: "center", padding: 32, fontSize: 12, color: C.textSub, border: `2px dashed ${C.border}`, borderRadius: 8 }}>
              No actions yet — add from the panel on the right
            </div>
          )}
          {present.map((k) => (
            <div key={k.key} style={{ marginBottom: 12, padding: 12, border: `1px solid ${C.border}`, borderRadius: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontWeight: 600 }}>{k.label}</span>
                <Btn variant="danger" size="sm" onClick={() => { const n = { ...actions }; delete n[k.key]; setActions(n); }}>Remove</Btn>
              </div>
              <ActionEditor
                kind={k.key}
                value={actions[k.key]}
                onChange={(p) => setActions({ ...actions, [k.key]: p })}
                actionTargetFields={actionTargetFields}
              />
            </div>
          ))}
        </div>
        <div style={{ ...cardStyle, padding: 12, marginTop: 12, background: "#fff8e1" }}>
          <Check
            checked={stopProcessing}
            onChange={(e) => setStopProcessing(e.target.checked)}
            align="start"
            label={<span><b>Stop Processing</b> — no further rules run when this rule matches</span>}
          />
        </div>
      </div>
      <div style={{ ...cardStyle, padding: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Add action</div>
        {missing.map((k) => (
          <Btn key={k.key} variant="outline" size="sm" style={{ width: "100%", marginBottom: 6, justifyContent: "flex-start" }} onClick={() => setActions({ ...actions, [k.key]: defaults[k.key] })}>
            <SvgIcon name="plus" size={12} color={C.primary} /> {k.label}
          </Btn>
        ))}
        {missing.length === 0 && <div style={{ fontSize: 11, color: C.textSub }}>All types added</div>}
      </div>
    </div>
  );
}

function JsonPanel({ json }) {
  const text = useMemo(() => JSON.stringify(json, null, 2), [json]);
  const copy = () => navigator.clipboard.writeText(text);
  const download = () => {
    const blob = new Blob([text], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rule.json";
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>Generated Rule JSON</span>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="outline" size="sm" onClick={copy}>Copy</Btn>
          <Btn variant="outline" size="sm" onClick={download}><SvgIcon name="download" size={12} color={C.text} /> Download</Btn>
        </div>
      </div>
      <pre style={{ margin: 0, padding: 16, fontSize: 11, fontFamily: "monospace", overflow: "auto", maxHeight: 480, background: "#f8f9fa" }}>{text}</pre>
    </div>
  );
}

function TraceView({ trace }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, marginLeft: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
        {trace.node.type === "all" ? "AND" : trace.node.type === "any" ? "OR" : "NOT"} — {trace.result ? "passed" : "failed"}
      </div>
      {trace.children.map((c, i) =>
        "children" in c ? (
          <TraceView key={i} trace={c} />
        ) : (
          <div key={i} style={{ fontSize: 11, padding: "6px 8px", marginBottom: 4, borderRadius: 6, background: c.result === "pass" ? "#d1f2db" : c.result === "fail" ? "#fdecea" : "#f1f5f9" }}>
            {fieldDef(c.node.field)?.label ?? c.node.field} → {c.result} (actual: {JSON.stringify(c.actual)})
          </div>
        ),
      )}
    </div>
  );
}

function defaultSimContext(catalog) {
  const base = catalog?.sample_simulation_context ?? SAMPLE_CONTEXT;
  const ce = base.charge_event ?? {};
  return {
    ...base,
    charge_event: {
      ...ce,
      event_time: ce.event_time ?? new Date().toISOString(),
    },
  };
}

function SimulationPanel({ rule, catalog, flash }) {
  const [text, setText] = useState(() => JSON.stringify(defaultSimContext(catalog), null, 2));
  const [run, setRun] = useState(null);
  const [serverResult, setServerResult] = useState(null);
  const [simBusy, setSimBusy] = useState(false);
  const [parseError, setParseError] = useState(null);

  const ctxObj = useMemo(() => {
    try {
      setParseError(null);
      return JSON.parse(text);
    } catch (e) {
      setParseError(String(e));
      return null;
    }
  }, [text]);

  const simulate = async () => {
    if (!ctxObj) return;
    const trace = evalGroup(rule.conditions, ctxObj);
    setRun({ trace, matched: trace.result });
    setSimBusy(true);
    setServerResult(null);
    try {
      const payload = {
        patient: ctxObj.patient,
        encounter: ctxObj.encounter,
        charge_event: {
          ...ctxObj.charge_event,
          event_time: ctxObj.charge_event?.event_time ?? new Date().toISOString(),
        },
        log: false,
      };
      const res = await evaluateManual(payload);
      setServerResult(res.evaluation ?? res);
      flash?.("Server evaluation complete", "success");
    } catch (err) {
      flash?.(err.message || "Server simulation failed", "error");
    } finally {
      setSimBusy(false);
    }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <div style={cardStyle}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700 }}>Input Context</span>
          <Btn size="sm" onClick={simulate} disabled={simBusy}>
            <SvgIcon name="flask" size={14} color="#fff" /> {simBusy ? "Running…" : "Run"}
          </Btn>
        </div>
        <div style={{ padding: 12 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {Object.keys(SIM_PRESETS).map((p) => (
              <Btn key={p} variant="outline" size="sm" onClick={() => {
                const preset = { ...SIM_PRESETS[p], charge_event: { ...SIM_PRESETS[p].charge_event, event_time: new Date().toISOString() } };
                setText(JSON.stringify(preset, null, 2));
              }}>{p}</Btn>
            ))}
          </div>
          <TextArea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={16}
            sx={{ "& .MuiOutlinedInput-root": { fontFamily: "monospace", fontSize: 11 } }}
          />
          {parseError && <div style={{ fontSize: 11, color: C.danger, marginTop: 6 }}>{parseError}</div>}
        </div>
      </div>
      <div style={cardStyle}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, fontWeight: 700 }}>Simulation Result</div>
        <div style={{ padding: 16 }}>
          {!run && <div style={{ fontSize: 12, color: C.textSub }}>Run simulation to see evaluation trace</div>}
          {run && (
            <>
              <div style={{
                padding: 12, borderRadius: 8, marginBottom: 12,
                background: run.matched ? "#d1f2db" : "#fdecea",
                color: run.matched ? "#145a32" : "#842029",
                fontWeight: 600,
              }}>
                {run.matched ? "Rule matched" : "Rule did not match"} — {rule.rule_name}
              </div>
              <TraceView trace={run.trace} />
              {run.matched && (
                <pre style={{ marginTop: 12, padding: 10, fontSize: 10, background: "#f8f9fa", borderRadius: 8, overflow: "auto" }}>
                  {JSON.stringify({ matched: true, applied_actions: buildRuleJSON(rule).actions }, null, 2)}
                </pre>
              )}
              {serverResult && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Server evaluation</div>
                  <pre style={{ padding: 10, fontSize: 10, background: "#f0f9ff", borderRadius: 8, overflow: "auto", border: `1px solid ${C.border}` }}>
                    {JSON.stringify(serverResult, null, 2)}
                  </pre>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TemplatesModal({ open, onClose, onPick }) {
  return (
    <Modal open={open} onClose={onClose} title="Rule Templates" description="Start from a common hospital billing rule.">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {TEMPLATES.map((t) => (
          <CardButton key={t.key} onClick={() => { onPick(t.key); onClose(); }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</div>
            <div style={{ marginTop: 6 }}><RuleTypeChip type={t.type} /></div>
          </CardButton>
        ))}
      </div>
    </Modal>
  );
}

export default function RuleBuilderScreen({ initial, setName, catalog, saving, onBack, onSave, flash }) {
  const [rule, setRule] = useState(initial);
  const [tab, setTab] = useState("builder");
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const fieldGroups = useMemo(
    () => enrichFieldGroups(catalog?.field_groups?.length ? catalog.field_groups : FIELD_GROUPS),
    [catalog],
  );
  const categories = catalog?.categories?.length ? catalog.categories : CATEGORIES;
  const ruleTypes = catalog?.rule_types?.length ? catalog.rule_types : RULE_TYPES;
  const operators = useMemo(() => resolveOperators(catalog?.operators), [catalog]);

  const actionTargetFields = useMemo(() => getActionTargetFields(fieldGroups), [fieldGroups]);

  const errors = useMemo(() => validateRule(rule), [rule]);
  const json = useMemo(() => buildRuleJSON(rule), [rule]);
  const update = (patch) => setRule((r) => ({ ...r, ...patch }));

  const handleSave = async () => {
    await onSave(rule);
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(json, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${rule.rule_code || "rule"}.json`;
    a.click();
    URL.revokeObjectURL(url);
    flash?.("JSON exported", "success");
  };

  const tabs = [
    { id: "builder", label: "Builder", icon: "sparkle" },
    { id: "actions", label: "Actions", icon: "bill" },
    { id: "json", label: "JSON Preview", icon: "sliders" },
    { id: "simulation", label: "Simulation", icon: "flask" },
  ];

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
        <div>
          <Btn variant="ghost" size="sm" onClick={onBack} sx={{ padding: 0, marginBottom: "6px", "&:hover": { background: "none", color: C.text } }}>
            <SvgIcon name="back" size={14} color={C.textSub} /> Back to rules
          </Btn>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Hospital Billing Rule Builder</h2>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: C.textSub }}>Rule Set: <b>{setName}</b> · Version v1.0</p>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <Btn variant="outline" size="sm" onClick={() => setTemplatesOpen(true)}><SvgIcon name="sparkle" size={14} color={C.primary} /> Templates</Btn>
          <Btn variant="outline" size="sm" onClick={() => flash?.(errors.length ? `Validation failed: ${errors.join(" · ")}` : "Validation passed", errors.length ? "error" : "success")}>
            <SvgIcon name="check" size={14} color={C.text} /> Validate
          </Btn>
          <Btn variant="outline" size="sm" onClick={() => setTab("simulation")}><SvgIcon name="flask" size={14} color={C.text} /> Simulate</Btn>
          <Btn variant="outline" size="sm" onClick={exportJSON}><SvgIcon name="download" size={14} color={C.text} /> Export</Btn>
          <Btn size="sm" onClick={handleSave} disabled={saving}>
            <SvgIcon name="check" size={14} color="#fff" /> {saving ? "Saving…" : "Save Rule"}
          </Btn>
        </div>
      </div>

      {errors.length > 0 && (
        <div style={{ padding: 10, borderRadius: 8, background: "#fdecea", border: "1px solid #f5c2c7", fontSize: 12, color: C.danger, marginBottom: 12, display: "flex", gap: 8 }}>
          <SvgIcon name="alert-triangle" size={16} color={C.danger} />
          <span><b>{errors.length} issue(s):</b> {errors.join(" · ")}</span>
        </div>
      )}

      <TabBar tabs={tabs} value={tab} onChange={setTab} />

      {tab === "builder" && (
        <div style={{ display: "grid", gridTemplateColumns: "240px 1fr 200px", gap: 16 }}>
          <FieldsSidebar fieldGroups={fieldGroups} categories={categories} />
          <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
            <MetadataForm rule={rule} update={update} ruleTypes={ruleTypes} />
            <div style={cardStyle}>
              <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Visual Rule Builder</div>
                <div style={{ fontSize: 11, color: C.textSub }}>Drag fields or add conditions · IF groups (AND · OR · NOT)</div>
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ ...labelStyle, marginBottom: 8 }}>IF the following conditions match</div>
                <GroupView node={rule.conditions} onChange={(g) => update({ conditions: g })} isRoot fieldGroups={fieldGroups} operators={operators} />
              </div>
            </div>
          </div>
          <BlocksSidebar />
        </div>
      )}

      {tab === "actions" && (
        <ActionsPanel
          actions={rule.actions}
          setActions={(a) => update({ actions: a })}
          stopProcessing={rule.stop_processing}
          setStopProcessing={(v) => update({ stop_processing: v })}
          actionTargetFields={actionTargetFields}
        />
      )}

      {tab === "json" && <JsonPanel json={json} />}
      {tab === "simulation" && <SimulationPanel rule={rule} catalog={catalog} flash={flash} />}

      <TemplatesModal
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        onPick={(k) => {
          setRule({ ...makeTemplate(k), rule_set_id: rule.rule_set_id });
          flash?.("Template loaded", "success");
        }}
      />
    </div>
  );
}
